<template>
    <BootstrapModal
        id="view-media-file-modal"
        class="e3-modal-fullscreen"
        size="xl"
        centered
        hide-footer
        @shown="onMediaModalShown"
        @hidden="onMediaModalHidden">
        <template #modal-title="{ titleId }">
            <div :id="titleId">
                <EInput
                    v-if="canEdit"
                    id="file-id"
                    v-model="form.name"
                    char-counter
                    :max-length="191"
                    :validation="$v.form.name"
                    :title="form.name"
                    class="view-file-input"/>
                <span v-else>{{ form.name }}</span>
            </div>
        </template>
        <div v-if="file">
            <div v-show="loading">
                <ELoader class="overlay-loader"/>
            </div>
            <transition name="fade" mode="out-in">
                <div class="position-relative">
                    <button v-if="hasPrevFile" class="d-none d-md-block e3-image-view-prev-btn" @click="loadPrev">
                        <ChevronLeftIcon/>
                    </button>
                    <button v-if="hasNextFile" class="d-none d-md-block e3-image-view-next-btn" @click="loadNext">
                        <ChevronRightIcon/>
                    </button>
                    <div class="e3-image-view-container">
                        <img v-if="!isVideo" :src="fileUrl" class="e3-img-full img-fluid" @load="onFileLoad"/>
                        <div v-else class="e3-full-video">
                            <EVideoPlayer :src="fileUrl" :poster="posterUrl" :type="file.mime_type" @ready="onFileLoad"/>
                        </div>
                    </div>
                    <div class="d-block d-md-none mt-2">
                        <button
                            v-if="hasPrevFile"
                            class="btn p-0 mr-2 btn-secondary text-primary"
                            @click="loadPrev">
                            <ChevronLeftIcon size="36"/>
                        </button>
                        <button
                            v-if="hasNextFile"
                            class="btn p-0 btn-secondary text-primary"
                            @click="loadNext">
                            <ChevronRightIcon size="36"/>
                        </button>
                    </div>
                    <div class="row mt-2">
                        <div class="col-sm-6 text-left">
                            <AutoGrowTextarea
                                v-if="canEdit"
                                v-model="form.description"
                                :maxlength="191"
                                char-counter
                                :disabled="actionLoading"
                                class="mb-2"/>
                            <p v-else class="ml-1 mb-0 text-left mb-2 align-items-baseline">{{ form.description }}</p>
                        </div>
                        <div class="col-sm-6 d-flex justify-content-between pl-sm-0 align-items-center align-items-sm-baseline">
                            <span class="mr-2">{{ $t('Uploaded at:') }} {{ formatDateTime(fileUploadedAt) }}</span>
                            <div class="d-flex justify-content-flex-end d-sm-block">
                                <div class="w-auto d-flex align-items-center justify-content-center mb-sm-2 mr-2 mr-sm-0">
                                    <RoundIconButton
                                        v-if="!actionLoading"
                                        type="btn-success"
                                        :disabled="saveLoading"
                                        :title="$t('Download')"
                                        class="mr-2"
                                        @click="download">
                                        <DownloadIcon size="18" class="text-white"/>
                                    </RoundIconButton>
                                    <ELoader v-else class="p-0 mr-2"/>
                                    <ConfirmButton
                                        v-if="canEdit"
                                        :disable="actionLoading || saveLoading"
                                        :msg="$t('Are you sure you want to delete the file', {name: file.name})"
                                        icon
                                        confirm-text="Delete"
                                        :title="$t('Delete file')"
                                        :confirm-header="$t('Delete file')"
                                        @confirmed="removeFile"/>
                                </div>
                                <EBtnLoader
                                    v-if="canEdit"
                                    color="success"
                                    :disabled="actionLoading"
                                    :is-submit-loading="saveLoading"
                                    @clicked="save">
                                    {{ $t('Save') }}
                                </EBtnLoader>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </div>
    </BootstrapModal>
</template>

<script>
import {mapGetters} from 'vuex';
import sortBy from 'lodash/sortBy';
import {minLength, required} from 'vuelidate/lib/validators';
import {alphaNumFileCustom} from '@/validators';
import {formatDateTime} from '@/filters';
import EventBus from '@/utils/eventBus';
import GalleryService from '@services/gallery.service';
import DiaryService from '@services/diary.service';
import ChildService from '@services/child.service';
import EVideoPlayer from '@components/video/VideoPlayer.vue';
import EInput from '@/common/components/input/Input.vue';
import BootstrapModal from '@components/modals/BootstrapModal.vue';
import {downloadS3File} from '@services/helper.service';
import ChevronLeftIcon from '@/common/components/svg-icons/ChevronLeftIcon.vue';
import ChevronRightIcon from '@/common/components/svg-icons/ChevronRightIcon.vue';
import DownloadIcon from '@/common/components/svg-icons/DownloadIcon.vue';

export default {
    name: 'ViewFileModal',
    props: {
        editable: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            file: null,
            files: [],
            type: null,
            subType: null,
            position: null,
            loading: true,
            form: {
                name: '',
                description: ''
            },
            actionLoading: false,
            saveLoading: false,
            videoMimeTypes: ['video/mp4', 'video/quicktime'],
            childId: null
        };
    },
    computed: {
        ...mapGetters([
            'activeKindergartenId',
            'userId',
            'canUploadDiaryImages',
            'folderFiles',
            'diaryMedia',
            'canEditChildFilesEmployee',
            'canManageChildFilesEmployee',
            'canEditChildFilesGuardian',
            'canManageChildFilesGuardian'
        ]),
        canEdit() {
            if(!this.editable) {
                return false;
            }
            if(this.type === 'gallery') {
                return this.$store.getters.folder.access === 'manage' ||
                    (this.$store.getters.folder.access === 'edit' && this.file.created_by === this.userId);
            } else if(this.type === 'diary') {
                return this.canUploadDiaryImages;
            } else if(this.type === 'child') {
                if (this.$store.getters.childProfile.view === 'guardian') {
                    return this.canManageChildFilesGuardian || (this.canEditChildFilesGuardian && this.file.created_by === this.userId);
                } else {
                    return this.canManageChildFilesEmployee || (this.canManageChildFilesEmployee && this.file.created_by === this.userId);
                }
            } else {
                return false;
            }
        },
        fileUploadedAt() {
            return this.file.uploaded_at;
        },
        hasPrevFile() {
            return this.position > 0;
        },
        hasNextFile() {
            return this.position >= 0 && this.position + 1 < this.sortedFiles.length;
        },
        isVideo() {
            return this.isVideoFile(this.file);
        },
        fileUrl() {
            return this.getFile(this.file);
        },
        posterUrl() {
            // Same URL as FileContainer's small thumb, so the poster is a cache hit.
            const thumb = this.file && this.file.thumbnails && this.file.thumbnails.small;
            if (!thumb) {
                return '';
            }
            if (thumb.url) {
                return thumb.url;
            }
            if (!thumb.filename) {
                return '';
            }
            if (this.type === 'child') {
                return `${process.env.VUE_APP_API_URL}/api/kindergartens/${this.activeKindergartenId}/children/${this.childId}/files_thumbs/${thumb.filename}`;
            }
            return `${process.env.VUE_APP_API_URL}/api/kindergartens/${this.activeKindergartenId}/files/${this.type}_thumbs/${thumb.filename}`;
        },
        sortedFiles() {
            if(this.type === 'gallery') { // or documents one day
                return this.folderFiles;
            } else if(this.type === 'diary' && this.subType) {
                if(this.subType === 'static') {
                    //get static images that are not in store because of multiple days in one view
                    return sortBy(this.files, f => f.uploaded_at);
                } else {
                    return sortBy(this.diaryMedia.filter(i => i.type === this.subType), f => f.uploaded_at);
                }
            } else if(this.type === 'child' || this.type === 'gallery_builtin_child' || this.type === 'gallery_builtin_diary') {
                return this.files; // Already sorted before sent in
            } else {
                return [];
            }
        }
    },
    methods: {
        formatDateTime, // TODO: Vue3 remove
        isVideoFile(file) {
            return !!(file && this.videoMimeTypes.includes(file.mime_type));
        },
        loadNext() {
            if (!this.hasNextFile) {
                return;
            }
            this.loading = true;
            this.position = this.position + 1;
            this.file = this.sortedFiles[this.position];
            this.preloadNeighbors();
        },
        loadPrev() {
            if (!this.hasPrevFile) {
                return;
            }
            this.loading = true;
            this.position = this.position - 1;
            this.file = this.sortedFiles[this.position];
            this.preloadNeighbors();
        },
        moveBetweenGalleryImages(e) {
            if (e.defaultPrevented) {
                return;
            }
            const el = e.target;
            if (el && (el.isContentEditable || (el.closest && el.closest('input, textarea, select')))) {
                return;
            }
            if (e.key === 'ArrowLeft') {
                this.loadPrev();
            } else if (e.key === 'ArrowRight') {
                this.loadNext();
            }
        },
        updateFormData() {
            this.form.name = this.file.name;
            this.form.description = this.file.description;
        },
        preloadImage(file) {
            if (!file || this.isVideoFile(file)) {
                return;
            }
            const img = new Image();
            img.src = this.getFile(file);
        },
        preloadNeighbors() {
            if (this.hasPrevFile) {
                this.preloadImage(this.sortedFiles[this.position - 1]);
            }
            if (this.hasNextFile) {
                this.preloadImage(this.sortedFiles[this.position + 1]);
            }
        },
        getFile(file) {
            if (file.url) {
                return file.url;
            }

            if (this.type === 'child') {
                return `${process.env.VUE_APP_API_URL}/api/kindergartens/${this.activeKindergartenId}/children/${this.childId}/files/${file.filename}`;
            }

            return `${process.env.VUE_APP_API_URL}/api/kindergartens/${this.activeKindergartenId}/files/${this.type}/${file.filename}`;
        },
        save() {
            this.$v.$touch();
            if (!this.$v.$invalid) {
                this.saveLoading = true;
                this.form.filename = this.file.filename;
                if (this.type === 'child') {
                    this.form.childId = this.childId;
                }
                this.$store.dispatch(`UPDATE_${this.type.toUpperCase()}_FILE`, this.form).then(() => {
                    this.$toastr.s(this.$t('Saved'));
                }).finally(() => {
                    this.saveLoading = false;
                });
            }
        },
        download() {
            this.actionLoading = true;

            if (this.file.url) {
                return downloadS3File(this.file.url, this.file, () => {
                    this.actionLoading = false;
                });
            }

            if(this.type === 'gallery') {
                GalleryService.downloadGalleryFile(this.activeKindergartenId, this.file).finally(() => {
                    this.actionLoading = false;
                });
            } else if(this.type === 'diary' || this.type === 'gallery_builtin_diary') {
                DiaryService.getDiaryFile(this.activeKindergartenId, this.file).finally(() => {
                    this.actionLoading = false;
                });
            } else if (this.type === 'child' || this.type === 'gallery_builtin_child') {
                ChildService.getChildFile(this.activeKindergartenId, this.childId, this.file).finally(() => {
                    this.actionLoading = false;
                });
            }
        },
        removeFile() {
            this.actionLoading = true;
            this.$store.dispatch(`DELETE_${this.type.toUpperCase()}_FILE`, {fileName: this.file.filename, childId: this.childId}).then(() => {
                EventBus.emit('hide-modal', 'view-media-file-modal');
                if (this.type === 'diary') {
                    EventBus.emit('diary-media-removed-event', this.file.filename);
                }
            }).finally(() => {
                this.actionLoading = false;
            });
        },
        onFileLoad(){
            this.loading = false;
            this.updateFormData();
        },
        onMediaModalShown() {
            document.addEventListener('keydown', this.moveBetweenGalleryImages);
        },
        onMediaModalHidden() {
            document.removeEventListener('keydown', this.moveBetweenGalleryImages);
        },
        onShowViewMediaFileModal(data) {
            this.file = data.file;
            if (data.files) {
                this.files = data.files;
            }
            this.type = data.type;
            if (data.subType) {
                this.subType = data.subType;
            }
            if (this.type === 'child' || this.type === 'gallery_builtin_child') {
                this.childId = this.$route.params.child_id;
            }
            this.form.name = this.file.name;
            this.form.description = this.file.description;
            this.position = this.sortedFiles.findIndex(f => f.id === this.file.id);
            EventBus.emit('show-modal', 'view-media-file-modal');
            this.preloadNeighbors();
            this.loading = true;
        }
    },
    components: {
        BootstrapModal,
        EInput,
        EVideoPlayer,
        ChevronLeftIcon,
        ChevronRightIcon,
        DownloadIcon
    },
    mounted() {
        EventBus.on('show-view-media-file-modal', this.onShowViewMediaFileModal);
    },
    beforeDestroy() {  // Vue 3 Rename beforeUnmount
        EventBus.off('show-view-media-file-modal', this.onShowViewMediaFileModal);
        document.removeEventListener('keydown', this.moveBetweenGalleryImages);
    },
    validations: {
        form: {
            name: {
                required,
                minLength: minLength(1),
                alphaNumFileCustom
            }
        }
    }
};
</script>
